import React, { useEffect, useState, useRef } from "react";
import {
  getCountryList,
  removeUserImage,
  updateUserData,
  uploadUserFile,
} from "../../../../services/CommonService";
import {
  CLIENT_LOCAL_STORE,
  COUNTRY_CODE,
  COUNTRY_ID,
  SALT,
} from "../../../../configs/AppConfig";
import { toast } from "react-toastify";
import Utils from "../../../../utils";
import validator from "validator";
import { encryptData } from "../../../../utils/crypto";
import { CustomInput, CustomSelect } from "../../../../components";

const UserProfile = (props) => {
  const [countryList, setCountryList] = useState([]);

  // BASIC INFO
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [countryId, setCountryId] = useState(COUNTRY_ID);
  const [countryCode, setCountryCode] = useState(COUNTRY_CODE);
  const [mobile, setMobile] = useState("");

  // ADDRESS INFO
  const [sameAddress, setSameAddress] = useState(false);
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [addressCountry, setAddressCountry] = useState(COUNTRY_ID);

  // IMAGE
  const [userImage, setUserImage] = useState("");
  const [isImage, setIsImage] = useState(false);
  const [keyFile, setKeyFile] = useState(Date.now());

  const [isViewReadOnly, setIsViewReadOnly] = useState(false);
  const modalRef = useRef(null);

  // LOAD COUNTRY LIST
  useEffect(() => {
    getCountryList()
      .then((response) => setCountryList(response.data.data))
      .catch(() => { });
  }, []);

  // LOAD USER DATA
  useEffect(() => {
    const userData = Utils.loginUserData();

    if (Object.keys(userData).length > 0) {
      setFirstName(userData.first_name);
      setLastName(userData.last_name);
      setEmail(userData.email);
      setMobile(userData.mobile);
      setCountryId(userData.country_id);
      setAddress1(userData.address_1 || "");
      setAddress2(userData.address_2 || "");
      setCity(userData.city || "");
      setState(userData.state || "");
      setZip(userData.zip || "");
      setAddressCountry(userData.address_country_id || COUNTRY_ID);
      setUserImage(userData.profile || "");

      if (userData.profile) setIsImage(true);

      const idx = countryList.findIndex((x) => x.id === parseInt(userData.country_id));
      if (idx > -1) setCountryCode(countryList[idx].code);

      // check same address
      checkSameAddress(props?.companyData, userData);

      // view-only
      if (userData.role_id > 4) setIsViewReadOnly(true);
    }
  }, [props?.companyData, countryList]);

  // VALIDATION ERRORS
  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    country_id: "",
    mobile: "",
    address1: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  // CHANGE COUNTRY
  function handleCountry(e) {
    const value = Number(e.target.value);
    setCountryId(value);
    const idx = countryList.findIndex((x) => x.id === value);
    if (idx > -1) setCountryCode(countryList[idx].code);
  }

  // FILE UPLOAD
  const handleFile = (e) => {
    if (e.target.files.length === 0) return;

    const file = e.target.files[0];
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG allowed");
      return;
    }

    props.setLoading(true);
    const fd = new FormData();
    fd.append("file", file);

    uploadUserFile(fd)
      .then((res) => {
        setUserImage(res.data.data.profile);
        setKeyFile(Date.now());
        setIsImage(true);

        const encrypted = encryptData(res.data.data, SALT);
        localStorage.setItem(CLIENT_LOCAL_STORE, encrypted);

        toast.success(res.data.message);
        props.setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        props.setLoading(false);
      });
  };

  // SUBMIT
  function onUpdateUser(e) {
    e.preventDefault();

    const err = {
      first_name: firstName ? "" : "First name required",
      last_name: lastName ? "" : "Last name required",
      email: !email
        ? "Email required"
        : !validator.isEmail(email)
          ? "Enter valid email"
          : "",
      country_id: countryId ? "" : "Select country",
      mobile: mobile ? "" : "Mobile required",
      city: city ? "" : "City required",
      state: state ? "" : "State required",
      zip: zip ? "" : "Zip required",
      address1: address1 ? "" : "Address required",
      country: addressCountry ? "" : "Select country",
    };

    setErrors(err);
    if (Object.values(err).some((x) => x)) return;

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      country: countryId,
      mobile,
      address_1: address1,
      address_2: address2,
      city,
      state,
      zip,
      address_country: addressCountry,
    };

    props.setLoading(true);
    updateUserData(payload)
      .then((res) => {
        const encrypted = encryptData(res.data.data, SALT);
        localStorage.setItem(CLIENT_LOCAL_STORE, encrypted);
        toast.success(res.data.message);
        props.setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        props.setLoading(false);
      });
  }

  // REMOVE IMAGE
  const handleRemoveImage = () => {
    props.setLoading(true);

    removeUserImage({})
      .then((res) => {
        setUserImage("");
        setIsImage(false);
        modalRef.current?.click();

        const encrypted = encryptData(res.data.data, SALT);
        localStorage.setItem(CLIENT_LOCAL_STORE, encrypted);

        toast.success(res.data.message);
        props.setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        props.setLoading(false);
      });
  };

  // CHECK SAME ADDRESS
  const checkSameAddress = (company, user) => {
    if (!company || !user) return;

    const isSame =
      company.address_1 === user.address_1 &&
      company.address_2 === user.address_2 &&
      company.city === user.city &&
      company.state === user.state &&
      company.zip_code === user.zip &&
      company.country_id === user.address_country_id;

    setSameAddress(isSame);
  };

  // COUNTRY OPTIONS
  const countryOptions = countryList.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  return (
    <div className="tab-pane active show setting-details-wrapper" id="nav-profile">

      <div className="setting-details-container">
        <div className="setting-form-container">
          <form onSubmit={onUpdateUser} className="setting-form">
            <div className="setting-form-section">
              <h3 className="setting-section-title">Basic Information</h3>

              <div className="setting-form-grid">
                <div className="setting-form-group">
                  <CustomInput
                    label="First Name"
                    name="first_name"
                    value={firstName}
                    required
                    error={errors.first_name}
                    disabled={isViewReadOnly}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label="Last Name"
                    value={lastName}
                    required
                    error={errors.last_name}
                    disabled={isViewReadOnly}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label="Email"
                    value={email}
                    required
                    error={errors.email}
                    disabled={isViewReadOnly}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomSelect
                    label="Country"
                    value={countryId}
                    options={countryOptions}
                    onChange={(e) => handleCountry(e)}
                    error={errors.country_id}
                    isDisabled={isViewReadOnly}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label={`Mobile Number (+${countryCode})`}
                    value={mobile}
                    required
                    error={errors.mobile}
                    disabled={isViewReadOnly}
                    onChange={(e) => setMobile(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ADDRESS SECTION */}
            <div className="setting-form-section">
              <h3 className="setting-section-title">Address Details</h3>

              <div className="mb-3">
                <input
                  type="checkbox"
                  checked={sameAddress}
                  disabled={isViewReadOnly}
                  onChange={(e) => setSameAddress(e.target.checked)}
                  id="sameAddress"
                />
                <label htmlFor="sameAddress" className="ms-2">
                  Same as company address
                </label>
              </div>

              <div className="setting-form-grid">
                <div className="setting-form-group">

                  <CustomInput
                    label="Address Line 1"
                    value={address1}
                    required
                    error={errors.address1}
                    disabled={isViewReadOnly}
                    onChange={(e) => setAddress1(e.target.value)}
                  />

                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label="Address Line 2"
                    value={address2}
                    disabled={isViewReadOnly}
                    onChange={(e) => setAddress2(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label="City"
                    value={city}
                    required
                    error={errors.city}
                    disabled={isViewReadOnly}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label="State"
                    value={state}
                    required
                    error={errors.state}
                    disabled={isViewReadOnly}
                    onChange={(e) => setState(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomInput
                    label="Zip Code"
                    value={zip}
                    required
                    error={errors.zip}
                    disabled={isViewReadOnly}
                    onChange={(e) => setZip(e.target.value)}
                  />
                </div>
                <div className="setting-form-group">
                  <CustomSelect
                    label="Country"
                    value={addressCountry}
                    options={countryOptions}
                    onChange={(opt) => setAddressCountry(opt.value)}
                    error={errors.country}
                    isDisabled={isViewReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* SAVE */}
            {!isViewReadOnly && (
              <div className="setting-form-actions">
                <button className="setting-btn-primary" type="submit">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Save 
                </button>
              </div>
            )}
          </form>
        </div>
        {/* SIDEBAR */}
        <div className="setting-header">
          <div className="setting-header-content">
            <h2 className="setting-header-title">User Information</h2>
            <p className="setting-header-subtitle">
              Manage your user details and profile
            </p>
          </div>

          <div className="setting-logo-section">
            <div className="setting-logo-container">
              <img
                src={userImage || "/images/user_bg.webp"}
                alt="User"
                className="setting-avatar"
              />
            </div>

            {!isViewReadOnly && (
              <div className="setting-logo-actions">
                <input
                  id="userImgUpload"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  className="setting-file-input-hidden"
                  key={keyFile}
                  onChange={handleFile}
                />
                {!isImage ? (
                  <label htmlFor="userImgUpload" className="setting-btn-upload">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"> <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> </svg>
                    Upload Logo
                  </label>
                ) : (
                  <button
                    className="setting-btn-remove"
                    data-bs-toggle="modal"
                    data-bs-target="#deleteUserModal"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"> <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> </svg>
                    Remove
                  </button>
                )}


              </div>
            )}
          </div>
        </div>
      </div>

      {/* DELETE MODAL */}
      <div className="modal fade" id="deleteUserModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">

            <div className="modal-header">
              <h5 className="modal-title">Delete Image</h5>
              <button
                ref={modalRef}
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              Are you sure you want to delete this image?
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" data-bs-dismiss="modal">
                Cancel
              </button>
              <button className="btn-danger" onClick={handleRemoveImage}>
                Delete
              </button>
            </div>

          </div>
        </div>
      </div>

    </div >
  );
};

export default UserProfile;
